import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'src/hooks/Debounce';
import { SVGAssets } from '@business/assets/vectors';
import {
  useGetSharedPeopleByIdQuery,
  useAddToShareMutation,
  useBulkUpdateShareMutation,
  useGetUnsharedPeopleByIdQuery,
  useDeleteShareMutation,
  DeleteShareApiResponse
} from '@ports/internal-services/share/shareSlice';
import {
  useInvitePeopleMutation,
  useGetUninvitedPeopleQuery,
  useGetInvitedPeopleQuery,
  DeleteInviteApiResponse,
  useDeleteInviteMutation
} from '@ports/internal-services/invite/inviteSlice';
import { useGetUserRolesQuery } from '@ports/internal-services/userRole/userRoleSlice';
import { useIsEmailExistMutation } from '@ports/internal-services/user/userSlice';
import { UserEmailExistResult } from '@hooks/useUserEmailExistCheck';
import {
  getToken,
  incrementShareRefreshCounter
} from '@ports/common/sessionStateSlice';
import { useSelector, useDispatch } from 'react-redux';
import { getUserData } from '@ports/common/userStateSlice';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { useUpdatePeopleMutation } from '@ports/internal-services/people/peopleSlice';
import { createImageFileFromUrl } from '@utils/helpers/lib/photoHelpers';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { ExistingPersonFound } from '../People/ExistingPersonFound';
import { ConfirmationBox } from '../ConfirmationBox';
import { Input } from '../Input';
import { Avatar } from '../Avatar';
import { Button } from '../Button';

interface PersonWithEmail {
  id: string;
  name: string;
  email: string;
  profileImage?: string;
  roleId?: string;
}

interface PersonWithoutEmail {
  id: string;
  name: string;
  profileImage?: string;
  pendingEmail?: string;
}

export const ShareModalComponent = ({
  isOpen,
  onClose,
  link,
  assetIds,
  currentUserId,
  mode = 'share',
  selectedRole,
  shareType = 'asset',
  showRoleSelector = false,
  onMessage
}: {
  isOpen: boolean;
  onClose: () => void;
  link: string;
  assetIds: string[];
  currentUserId?: string;
  mode?: 'share' | 'invite' | 'unshare';
  selectedRole?: string;
  /** Determines which entity type is being shared — passed as `?type=` to /sharedPeople */
  shareType?: 'asset' | 'recipe' | 'memory' | 'people';
  /** When true, renders a role-selector in the search bar (like invite flow) and passes roleId per person to addToShare */
  showRoleSelector?: boolean;
  onMessage?: (type: 'success' | 'error', message: string) => void;
}) => {
  const { t } = useTranslation();
  const isUnshareMode = mode === 'unshare';
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const userDetails = useSelector(getUserData);
  const [searchText, setSearchText] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPeopleWithEmail, setSelectedPeopleWithEmail] = useState<
    PersonWithEmail[]
  >([]);
  const [selectedPeopleWithoutEmail, setSelectedPeopleWithoutEmail] = useState<
    PersonWithoutEmail[]
  >([]);
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});
  const [isUpdatingEmails, setIsUpdatingEmails] = useState(false);
  const [addEmailError, setAddEmailError] = useState<string>('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [personToRemove, setPersonToRemove] = useState<string | null>(null);
  // In invite mode: person selected from the search dropdown but not yet committed
  // to the staging chips. "Add" button commits this to selectedPeopleWithEmail.
  const [pendingInviteSelection, setPendingInviteSelection] =
    useState<PersonWithEmail | null>(null);
  const [existingUser, setExistingUser] = useState<UserEmailExistResult | null>(
    null
  );

  // Custom Role Dropdown State
  const [selectedRoleForInvite, setSelectedRoleForInvite] = useState<
    string | undefined
  >(selectedRole);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(searchText, 400);
  const [UpdatePeople] = useUpdatePeopleMutation();

  // Fetch user roles for invite mode
  const { data: userRolesData, isLoading: rolesLoading } = useGetUserRolesQuery(
    { offset: 0, limit: 100 },
    { skip: mode !== 'invite' && !showRoleSelector }
  );

  const userRolesOptions = useMemo(() => {
    return userRolesData?.data?.rows?.filter((role: any) =>
      mode !== 'invite' ? role?.roleName !== 'CoOwner' : true
    );
  }, [userRolesData?.data?.rows, mode]);

  useEffect(() => {
    if (
      (mode === 'invite' || showRoleSelector) &&
      !selectedRoleForInvite && // Only set if no role is currently selected
      userRolesOptions &&
      userRolesOptions.length > 0
    ) {
      setSelectedRoleForInvite(userRolesOptions[0].id);
    }
  }, [mode, userRolesData, selectedRoleForInvite]);
  // Fetch unshared people for share mode
  const { data: peopleDataForShareMode } = useGetUnsharedPeopleByIdQuery(
    { name: debouncedSearch, id: assetIds?.[0] ?? '', type: shareType },
    { skip: !debouncedSearch || !assetIds?.[0] || mode !== 'share' }
  );

  // Fetch uninvited people for invite mode
  const { data: peopleDataForInviteMode } = useGetUninvitedPeopleQuery(
    { name: debouncedSearch },
    { skip: !debouncedSearch || mode !== 'invite' }
  );

  // Use the appropriate data based on mode
  const peopleDataForSearch =
    mode === 'invite' ? peopleDataForInviteMode : peopleDataForShareMode;
  const [addToShare] = useAddToShareMutation();
  const [bulkUpdateShare] = useBulkUpdateShareMutation();
  const [deleteShare] = useDeleteShareMutation();
  const [deleteInvite] = useDeleteInviteMutation();
  const [invitePeople] = useInvitePeopleMutation();
  const [isEmailExist] = useIsEmailExistMutation();

  const { data: sharedPeopleData, refetch } = useGetSharedPeopleByIdQuery(
    { id: assetIds?.[0] ?? '', type: shareType },
    { skip: !assetIds?.[0] }
  );
  const { data: invitedPeopleData, refetch: refetchInvited } =
    useGetInvitedPeopleQuery(
      { name: '' }, // Pass an empty string or debouncedSearch based on your API args
      { skip: mode !== 'invite' } // Only run in invite mode
    );
  useEffect(() => {
    if (mode === 'invite') {
      refetchInvited();
    } else if (assetIds?.[0]) {
      refetch();
    }
  }, []);
  const peopleWithAccessList = useMemo(() => {
    if (mode === 'invite') {
      const data = invitedPeopleData?.data;
      return Array.isArray(data) ? data : data?.rows || [];
    } else {
      const data: any = sharedPeopleData?.data;
      return Array.isArray(data) ? data : data?.rows || [];
    }
  }, [mode, invitedPeopleData, sharedPeopleData]);
  // Split people into those with email and those without
  const { peopleWithEmail, peopleWithoutEmail } = useMemo(() => {
    let rows: any[] = [];

    if (mode === 'invite' && peopleDataForInviteMode?.data) {
      // For invite mode, handle the rows appropriately
      const inviteRows = peopleDataForInviteMode.data;
      // Convert to array if it's a single object, otherwise use as array
      rows = Array.isArray(inviteRows) ? inviteRows : [inviteRows];
    } else if (mode === 'share' && peopleDataForShareMode?.data?.rows) {
      // For share mode
      rows = Array.isArray(peopleDataForShareMode.data.rows)
        ? peopleDataForShareMode.data.rows
        : [peopleDataForShareMode.data.rows];
    }

    const withEmail: PersonWithEmail[] = [];
    const withoutEmail: PersonWithoutEmail[] = [];

    if (!rows || rows.length === 0) {
      return { peopleWithEmail: withEmail, peopleWithoutEmail: withoutEmail };
    }

    rows.forEach((item: any) => {
      if (item?.email) {
        withEmail.push({
          id: item.id,
          name: item.name,
          email: item.email,
          profileImage: item.profileImage
        });
      } else {
        // For invite mode or people without emails
        withoutEmail.push({
          id: item.id,
          name: item.name,
          profileImage: item.profileImage
        });
      }
    });
    return { peopleWithEmail: withEmail, peopleWithoutEmail: withoutEmail };
  }, [
    peopleDataForSearch?.data?.rows,
    mode,
    peopleDataForInviteMode,
    peopleDataForShareMode
  ]);

  const firstAssetId = assetIds?.[0] ?? '';

  useEffect(() => {
    if (firstAssetId) {
      refetch();
    }
  }, [firstAssetId, refetch]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle search dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
      // Handle role dropdown
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPersonWithEmail = (person: PersonWithEmail) => {
    const isAlreadySelected = selectedPeopleWithEmail.find(
      p => p.id === person.id
    );
    if (!isAlreadySelected) {
      setSelectedPeopleWithEmail([...selectedPeopleWithEmail, person]);
    } else {
      setSelectedPeopleWithEmail(prev => prev.filter(p => p.id !== person.id));
    }
    setSearchText('');
    setShowDropdown(false);
  };

  // Commit the staged (pending) invite selection to the chips list.
  const handleAddPendingSelection = () => {
    if (!pendingInviteSelection) return;
    handleSelectPersonWithEmail(
      showRoleSelector
        ? { ...pendingInviteSelection, roleId: selectedRoleForInvite }
        : pendingInviteSelection
    );
    setPendingInviteSelection(null);
    setSearchText('');
  };

  const handleSelectPersonWithoutEmail = (person: PersonWithoutEmail) => {
    if (!selectedPeopleWithoutEmail.find(p => p.id === person.id)) {
      setSelectedPeopleWithoutEmail([...selectedPeopleWithoutEmail, person]);
    }
    setSearchText('');
    setShowDropdown(false);
    setShowAddEmailModal(true);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleUpdateEmail = (personId: string, email: string) => {
    setSelectedPeopleWithoutEmail(prev =>
      prev.map(p => (p.id === personId ? { ...p, pendingEmail: email } : p))
    );

    if (emailErrors[personId]) {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[personId];
        return newErrors;
      });
    }
  };

  const handleRemovePersonWithoutEmail = (personId: string) => {
    setSelectedPeopleWithoutEmail(prev => prev.filter(p => p.id !== personId));
    if (emailErrors[personId]) {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[personId];
        return newErrors;
      });
    }
  };

  const handleExistUserProfileImageUpload = useCallback(
    async (profilePictureFile: File, personId: string) => {
      if (profilePictureFile) {
        try {
          const idToken = token || '';
          if (!idToken) {
            throw new Error('Authentication token not found');
          }

          // Upload to S3
          const imageUrl = await uploadToS3(
            profilePictureFile,
            idToken,
            userDetails?.userId || '',
            'profile',
            '',
            {
              userId: userDetails?.userId
            }
          );

          // Update people with profile image
          const updateImageDataObj = {
            id: personId,
            updatePeople: {
              profileImage: imageUrl
            }
          };

          await UpdatePeople(updateImageDataObj);
        } catch (uploadError: any) {
          console.error('Profile picture upload error:', uploadError);
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t('Profile picture upload failed. Please try again.')
            }
          });
        }
      }
    },
    [UpdatePeople, token, userDetails]
  );

  const handleSaveEmails = async () => {
    const errors: Record<string, string> = {};
    const emailsToUpdate: { peopleId: string; email: string }[] = [];

    for (const person of selectedPeopleWithoutEmail) {
      if (!person.pendingEmail) {
        errors[person.id] = 'Email is required';
      } else if (!validateEmail(person.pendingEmail)) {
        errors[person.id] = 'Invalid email format';
      } else {
        // Check if email already exists in the system
        try {
          const res = await isEmailExist({
            isEmailExist: {
              email: person.pendingEmail
            }
          }).unwrap();

          if (res?.data) {
            errors[person.id] = t(`serverMessages.${res.message}`, res?.data);
            if (res.message !== 'PEOPLE-105') {
              const userData = res.data as any;
              setExistingUser({
                userId: userData?.userId,
                name: `${userData?.userDetail?.firstName || ''} ${userData?.userDetail?.lastName || ''}`.trim(),
                profileImage: userData?.userDetail?.profileImage
              });
            }
          } else {
            setExistingUser(null); // Clear existing user info if email does not exist
            emailsToUpdate.push({
              peopleId: person.id,
              email: person.pendingEmail
            });
          }
        } catch (err: any) {
          if (err?.status === 404) {
            emailsToUpdate.push({
              peopleId: person.id,
              email: person.pendingEmail
            });
          }
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setEmailErrors(errors);
      return;
    }

    if (emailsToUpdate.length === 0) {
      setShowAddEmailModal(false);
      return;
    }

    try {
      setIsUpdatingEmails(true);
      const res = await bulkUpdateShare({
        bulkUpdateShare: {
          emails: emailsToUpdate
        }
      }).unwrap();

      if (res?.ok) {
        const updatedPeople = selectedPeopleWithoutEmail
          .filter(p => p.pendingEmail)
          .map(p => ({
            id: p.id,
            name: p.name,
            email: p.pendingEmail!,
            profileImage: p.profileImage
          }));

        setSelectedPeopleWithEmail(prev => [...prev, ...updatedPeople]);
        setSelectedPeopleWithoutEmail([]);
        setEmailErrors({});
        setAddEmailError('');
        setShowAddEmailModal(false);
        if (mode === 'invite') {
          await refetchInvited();
        } else {
          await refetch();
        }
      } else {
        setAddEmailError(
          res?.message
            ? t(`messageCode.${res.message}`, res.message)
            : t('share.emailsUpdateError') || 'Failed to update emails'
        );
      }
    } catch (err: any) {
      setAddEmailError(
        err?.data?.message
          ? t(`messageCode.${err.data.message}`, err.data.message)
          : err?.message || 'An error occurred while updating emails'
      );
    } finally {
      setIsUpdatingEmails(false);
    }
  };

  const handleRemoveFromAccess = async (personId: string) => {
    if (!personId) return;

    try {
      let res: DeleteInviteApiResponse | DeleteShareApiResponse = null;
      if (mode !== 'invite') {
        res = await deleteShare({
          id: personId
        }).unwrap();
      } else {
        res = await deleteInvite({
          id: personId
        }).unwrap();
      }

      if (res?.ok) {
        // await refetch();
        onClose();
        onMessage?.(
          'success',
          t('share.removeSuccess') || 'Person removed successfully'
        );
        return;
      } else {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: res?.message
              ? t(`messageCode.${res.message}`, res.message)
              : t('share.removeError') || 'Failed to remove person'
          }
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.data?.message
            ? t(`messageCode.${err.data.message}`, err.data.message)
            : err?.message || 'An error occurred while removing person'
        }
      });
    }
  };

  const handleShowRemoveConfirmation = (personId: string) => {
    setPersonToRemove(personId);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = () => {
    if (personToRemove) {
      handleRemoveFromAccess(personToRemove);
    }
    setShowRemoveConfirm(false);
    setPersonToRemove(null);
  };

  const handleSave = async () => {
    if (isUnshareMode) {
      onClose();
      return;
    }

    // For invite mode
    if (mode === 'invite') {
      return handleInvitePeople();
    }

    // For share mode
    return handleSharePeople();
  };

  const handleInvitePeople = async () => {
    // Validation: Check if people are selected
    const selectedPeopleIds = selectedPeopleWithEmail.map(p => p.id);

    if (selectedPeopleIds.length === 0) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: 'Please select at least one person to invite'
        }
      });
      return;
    }

    // Validation: Check if role is selected
    if (!selectedRoleForInvite) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: 'Please select a role for the invitees'
        }
      });
      return;
    }

    try {
      setIsInviting(true);

      // Build payload with people and their roles
      const peoplePayload = selectedPeopleIds.map(id => ({
        id,
        roleId: selectedRoleForInvite
      }));

      const res = await invitePeople({
        sharedAssetBody: {
          peopleId: peoplePayload as any
        }
      }).unwrap();

      if (res?.ok) {
        await refetchInvited();
        onClose();
        onMessage?.('success', 'Invitations sent successfully');
        return;
      } else {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: res?.message
              ? t(`messageCode.${res.message}`, res.message)
              : 'Failed to send invitations'
          }
        });
      }
    } catch (err: any) {
      console.error('Invite error:', err);
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.data?.message
            ? t(`messageCode.${err.data.message}`, err.data.message)
            : err?.message || 'An error occurred while sending invitations'
        }
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleSharePeople = async () => {
    const allSelectedIds = selectedPeopleWithEmail.map(p => p.id);

    // Provide default fallback to close cleanly for invite mode if save clicked empty
    if (allSelectedIds.length === 0 && mode === 'invite') {
      onClose();
      return;
    }

    if (allSelectedIds.length === 0) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: 'Please select at least one person with email to share'
        }
      });
      return;
    }

    try {
      const peopleIdPayload = showRoleSelector
        ? selectedPeopleWithEmail.map(p => ({ id: p.id, roleId: p.roleId }))
        : allSelectedIds;

      await addToShare({
        addToShare: {
          id: assetIds,
          peopleId: peopleIdPayload as any,
          type: shareType
        }
      }).unwrap();

      // unwrap() throws on non-2xx; reaching here means the API call succeeded
      dispatch(incrementShareRefreshCounter());
      await refetch();
      setSelectedPeopleWithEmail([]);
      setSelectedPeopleWithoutEmail([]);
      setSearchText('');
      onClose();
      onMessage?.('success', t('share.saveSuccess') || 'Shared successfully');
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.data?.message
            ? t(`messageCode.${err.data.message}`, err.data.message)
            : err?.message || 'An error occurred'
        }
      });
    }
  };

  const handleDiscard = () => {
    setSelectedPeopleWithEmail([]);
    setSelectedPeopleWithoutEmail([]);
    setSearchText('');
    setEmailErrors({});
    setShowAddEmailModal(false);
    onClose();
  };

  const handleDiscardAddMailChanges = () => {
    setSelectedPeopleWithoutEmail([]);
    setEmailErrors({});
    setShowAddEmailModal(false);
  };

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  };

  const handleLinkExistingPerson = useCallback(async () => {
    const personId = Object.keys(emailErrors)?.[0];
    if (personId) {
      const linkDataObj = {
        ...existingUser
      };

      if (linkDataObj?.profileImage) {
        const file = await createImageFileFromUrl(
          linkDataObj.profileImage,
          `${linkDataObj?.name.replace(/\s/g, '_')}.jpg`
        );
        handleExistUserProfileImageUpload(file, personId);
      }
      setExistingUser(null);

      try {
        const currentPerson = selectedPeopleWithoutEmail.find(
          p => p.id === personId
        );
        const updateImageDataObj = {
          id: personId,
          updatePeople: {
            // name: linkDataObj.name,
            name: currentPerson?.name || '',
            email: currentPerson?.pendingEmail || ''
          }
        };

        await UpdatePeople(updateImageDataObj);

        const updatedPeople = selectedPeopleWithoutEmail
          .filter(p => p.id === personId)
          .map(p => ({
            id: p.id,
            // name: linkDataObj.name,
            name: p.name,
            email: p.pendingEmail!,
            profileImage: linkDataObj.profileImage
          }));

        setSelectedPeopleWithEmail(prev => [...prev, ...updatedPeople]);
        setSelectedPeopleWithoutEmail([]);
        setEmailErrors({});
        setAddEmailError('');
        setShowAddEmailModal(false);
      } catch (err: any) {
        console.error('Error linking existing person:', err);
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message:
              t('share.linkExistingPersonError') ||
              'Failed to link existing person'
          }
        });
      }
    }
  }, [emailErrors, selectedPeopleWithoutEmail, existingUser, setExistingUser]);

  if (!isOpen) return null;

  // Add Email Modal
  if (showAddEmailModal && selectedPeopleWithoutEmail.length > 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-opacity">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
          onClick={handleModalClick}>
          {/* Header */}
          <div className="px-8 pt-8 pb-4">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <Button
                  btnStyle="no-outline"
                  onClick={handleDiscardAddMailChanges}
                  className="text-gray-600 hover:text-gray-800 p-1 -ml-4">
                  <SVGAssets.ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <h2 className="text-xl font-bold text-gray-800">
                  {t('share.addMailId')}
                </h2>
              </div>
              <Button
                btnStyle="no-outline"
                onClick={handleDiscard}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full">
                <SVGAssets.CloseIcon className="w-6 h-6" />
              </Button>
            </div>

            <div className="border-b border-gray-100 mb-6 mx-[-2rem]" />

            {/* People without mail list */}
            <div className="mb-6">
              <h3 className="text-gray-800 font-semibold mb-4">
                {t('share.peopleWithoutMail')}
              </h3>
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {selectedPeopleWithoutEmail.map(person => (
                  <div
                    key={person.id}
                    className="flex items-center gap-4 justify-between">
                    <div className="flex items-center gap-3 min-w-[120px]">
                      <Avatar
                        alt={person.name}
                        imgClassName="rounded-full w-12 h-12"
                        src={person.profileImage}
                        size="h-12 w-12"
                        isHeader={true}
                      />
                      <span className="font-medium text-gray-800">
                        {person.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1">
                        <Input
                          type="email"
                          placeholder="Email id"
                          value={person.pendingEmail || ''}
                          onChange={e =>
                            handleUpdateEmail(person.id, e.target.value)
                          }
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            emailErrors[person.id]
                              ? 'border-red-400 focus:ring-red-400/20 focus:border-red-400'
                              : 'border-gray-300 focus:ring-[#456b35]/20 focus:border-[#456b35]'
                          }`}
                        />
                        {emailErrors[person.id] && (
                          <p className="text-red-500 text-xs mt-1">
                            {emailErrors[person.id]}
                          </p>
                        )}
                      </div>
                      {/* <Button
                        btnStyle="no-outline"
                        onClick={() => {}}
                        className="text-[#456b35] p-1">
                        <SVGAssets.AlertCheck className="w-5 h-5 text-[#456b35]" />
                      </Button> */}
                      <Button
                        btnStyle="no-outline"
                        onClick={() =>
                          handleRemovePersonWithoutEmail(person.id)
                        }
                        className="text-red-400 hover:text-red-500 p-1">
                        <SVGAssets.CloseIcon className="w-5 h-5 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white px-8 pb-8 pt-6 border-t border-gray-100">
            <div className="flex justify-center gap-4">
              <Button
                btnStyle="no-outline"
                onClick={handleDiscardAddMailChanges}
                className="w-40 py-2.5 rounded-lg font-semibold text-[15px] border border-[#8FB358] bg-[#f5faeb] text-[#4a6b32] hover:bg-[#ebf5dd] transition-colors text-center">
                {t('share.discardChanges')}
              </Button>
              <Button
                btnStyle="no-outline"
                onClick={handleSaveEmails}
                disabled={isUpdatingEmails}
                className="w-40 py-2.5 rounded-lg font-semibold text-[15px] bg-[#4a6b32] text-white hover:bg-[#3d592a] transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed">
                {isUpdatingEmails ? t('saving') : t('share.saveChanges')}
              </Button>
            </div>
            {addEmailError && (
              <p className="mt-3 text-center text-sm text-red-500">
                {addEmailError}
              </p>
            )}
          </div>
        </div>

        {existingUser && (
          <ExistingPersonFound
            existingUser={existingUser}
            handleLinkExistingPerson={handleLinkExistingPerson}
            handleUseDifferentEmail={() => {
              setExistingUser(null);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-opacity">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${mode === 'invite' ? 'max-w-[600px]' : 'max-w-xl'} max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={handleModalClick}>
        {/* Header */}
        <div className="px-8 pt-6 pb-4 flex-shrink-0">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[22px] font-bold text-gray-800">
              {mode === 'invite'
                ? t('share.invite')
                : isUnshareMode
                  ? t('unshare')
                  : t('share.title')}
            </h2>
            <Button
              btnStyle="no-outline"
              onClick={handleDiscard}
              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-colors">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          </div>

          {/* Selected People with Email (Only visible in Share flow) */}
          {selectedPeopleWithEmail.length > 0 && (
            <div className="flex gap-4 mb-4 overflow-x-auto pb-2">
              {selectedPeopleWithEmail.map(person => (
                <div key={person.id} className="relative flex-shrink-0">
                  <div className="flex flex-col items-center gap-2">
                    <div className="relative">
                      <Avatar
                        alt={person.name}
                        imgClassName="rounded-full w-14 h-14 object-cover"
                        src={person.profileImage}
                        size="h-14 w-14"
                        isHeader={true}
                      />
                      <button
                        onClick={() => {
                          setSelectedPeopleWithEmail(prev =>
                            prev.filter(p => p.id !== person.id)
                          );
                        }}
                        aria-label={`Remove ${person.name}`}
                        className="absolute top-0 right-0 w-5 h-5 bg-[#EF4444] rounded-full flex items-center justify-center border-[2px] border-white hover:bg-red-600 transition-colors">
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 10 10"
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round">
                          <line x1="1" y1="1" x2="9" y2="9" />
                          <line x1="9" y1="1" x2="1" y2="9" />
                        </svg>
                      </button>
                    </div>
                    <span className="text-sm font-medium text-gray-800 max-w-[80px] text-center truncate">
                      {person.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {mode === 'share' ? (
            <div className="border-b border-gray-100 mb-4 mx-[-2rem]" />
          ) : (
            <div className="mb-4" />
          )}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-8">
          {/* Search Input Area */}
          {!isUnshareMode && (
            <div className="relative mb-6" ref={dropdownRef}>
              {mode === 'invite' || showRoleSelector ? (
                <div className="md:flex items-center gap-3">
                  {/* Unified Input and Custom Role Box for Invite/Share-with-Roles Flow */}
                  <div className="md:flex-1 md:flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#456b35]/20 focus-within:border-[#456b35] transition-all p-1">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={
                        mode === 'invite'
                          ? t('share.searchForPeopleToInvite')
                          : t('share.searchForPeopleToShare')
                      }
                      value={searchText}
                      onChange={e => {
                        setSearchText(e.target.value);
                        setShowDropdown(true);
                        // Typing new text discards any staged selection
                        if (pendingInviteSelection)
                          setPendingInviteSelection(null);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      className="flex-1 px-4 py-3  text-gray-600 border-none outline-none focus:ring-0 bg-transparent rounded-l-lg"
                    />

                    {/* Custom Role Dropdown */}
                    <div
                      ref={roleDropdownRef}
                      className="relative flex items-center gap-1 pl-2 pr-4 bg-white border-l border-transparent cursor-pointer rounded-r-lg"
                      onClick={() =>
                        setIsRoleDropdownOpen(!isRoleDropdownOpen)
                      }>
                      <div
                        className="relative group/roletooltip"
                        onClick={e => e.stopPropagation()}>
                        <Info className="w-[18px] h-[18px] text-[#4a6b32]" />
                        {userRolesOptions?.find(
                          (r: any) => r.id === selectedRoleForInvite
                        )?.description && (
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover/roletooltip:block w-56 p-2.5 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-[60] pointer-events-none leading-relaxed">
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-800" />
                            {
                              userRolesOptions?.find(
                                (r: any) => r.id === selectedRoleForInvite
                              )?.description
                            }
                          </div>
                        )}
                      </div>
                      <span className="py-3 bg-transparent text-[#4a6b32] font-semibold text-[15px] select-none capitalize">
                        {rolesLoading
                          ? 'Loading...'
                          : userRolesOptions?.find(
                              (r: any) => r.id === selectedRoleForInvite
                            )?.roleName ||
                            userRolesOptions?.[0]?.roleName ||
                            t('share.selectRole')}
                      </span>
                      <svg
                        className={`w-4 h-4 text-[#4a6b32] transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>

                      {/* Custom Dropdown Menu */}
                      {isRoleDropdownOpen && (
                        <div className="absolute top-full right-0 mt-2 w-[180px] bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 max-h-[220px] overflow-y-auto">
                          {userRolesOptions.map((role: any) => (
                            <div
                              key={role.id}
                              className={`px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-[14px] font-medium capitalize ${
                                selectedRoleForInvite === role.id
                                  ? 'text-[#4a6b32] bg-[#f5faeb]'
                                  : 'text-gray-700'
                              }`}
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedRoleForInvite(role.id);
                                setIsRoleDropdownOpen(false);
                              }}>
                              {role.roleName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={handleAddPendingSelection}
                    disabled={!pendingInviteSelection}
                    className="bg-[#2C2C2C] hover:bg-black text-white px-8 py-[13px] rounded-lg font-semibold text-[15px] flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed md:mt-0 mt-4">
                    {t('add')}
                  </Button>
                </div>
              ) : (
                /* Original Search Input for Share Flow */
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder={
                    t('share.searchPlaceholder') || 'Search for people to share'
                  }
                  value={searchText}
                  onChange={e => {
                    setSearchText(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#456b35]/20 focus:border-[#456b35]"
                />
              )}

              {/* Search Dropdown */}
              {showDropdown && searchText && (
                <div className="absolute top-full left-0 z-40 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[350px] overflow-y-auto">
                  {/* For Invite Mode - Show all people without email distinction */}
                  {mode === 'invite' ? (
                    <>
                      {peopleWithEmail.length > 0 && (
                        <div className="p-4 pb-2">
                          <h4 className="text-[15px] font-semibold text-gray-900 mb-3">
                            {t('share.peopleToInvite')}
                          </h4>
                          <div className="space-y-3">
                            {peopleWithEmail.map(person => (
                              <button
                                key={person.id}
                                onClick={() => {
                                  // Stage (highlight) the selection — user must
                                  // click "Add" to commit to the chips list.
                                  setPendingInviteSelection(person);
                                  setSearchText(person.name);
                                  setShowDropdown(false);
                                }}
                                className={`w-full flex items-center gap-4 p-1 rounded-lg transition-colors text-left ${
                                  pendingInviteSelection?.id === person.id
                                    ? 'bg-[#f0f7e6] border border-[#8FB358]'
                                    : 'hover:bg-gray-50'
                                }`}>
                                <Avatar
                                  alt={person.name}
                                  imgClassName="rounded-full w-12 h-12 object-cover"
                                  src={person.profileImage}
                                  size="h-12 w-12"
                                  isHeader={true}
                                />
                                <div>
                                  <p className="font-medium text-gray-900 text-[15px]">
                                    {person.name}
                                  </p>
                                  <p className="text-[13px] text-gray-500">
                                    {person.email}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {peopleWithoutEmail.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-[15px] font-semibold text-gray-900 mb-3">
                            {t('share.peopleToInvite')}
                          </h4>
                          <div className="space-y-3">
                            {peopleWithoutEmail.map(person => (
                              <button
                                key={person.id}
                                onClick={() =>
                                  handleSelectPersonWithoutEmail(person)
                                }
                                className="w-full flex items-center gap-4 p-1 rounded-lg hover:bg-gray-50 transition-colors text-left">
                                <Avatar
                                  alt={person.name}
                                  imgClassName="rounded-full w-12 h-12 object-cover"
                                  src={person.profileImage}
                                  size="h-12 w-12"
                                  isHeader={true}
                                />
                                <div>
                                  <p className="font-medium text-gray-900 text-[15px]">
                                    {person.name}
                                  </p>
                                  <p className="text-[13px] text-gray-400">
                                    {t('share.clickToAddMailId')}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {peopleWithoutEmail.length === 0 &&
                        peopleWithEmail.length === 0 && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {t('share.noMatchingPeopleFound')}
                          </div>
                        )}
                    </>
                  ) : (
                    <>
                      {/* People with mail */}
                      {peopleWithEmail.length > 0 && (
                        <div className="p-4">
                          <h4 className="text-[15px] font-semibold text-gray-900 mb-3">
                            {t('share.peopleWithMail')}
                          </h4>
                          <div className="space-y-3">
                            {peopleWithEmail.map(person => (
                              <button
                                key={person.id}
                                onClick={() => {
                                  if (showRoleSelector) {
                                    setPendingInviteSelection(person);
                                    setSearchText(person.name);
                                    setShowDropdown(false);
                                  } else {
                                    handleSelectPersonWithEmail(person);
                                  }
                                }}
                                className={`w-full flex items-center gap-4 p-1 rounded-lg transition-colors text-left ${
                                  showRoleSelector &&
                                  pendingInviteSelection?.id === person.id
                                    ? 'bg-[#f0f7e6] border border-[#8FB358]'
                                    : 'hover:bg-gray-50'
                                }`}>
                                <Avatar
                                  alt={person.name}
                                  imgClassName="rounded-full w-12 h-12 object-cover"
                                  src={person.profileImage}
                                  size="h-12 w-12"
                                  isHeader={true}
                                />
                                <div>
                                  <p className="font-medium text-gray-900 text-[15px]">
                                    {person.name}
                                  </p>
                                  <p className="text-[13px] text-gray-500">
                                    {person.email}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* People without mail */}
                      {peopleWithoutEmail.length > 0 && (
                        <div className="p-4 pt-2">
                          <h4 className="text-[15px] font-semibold text-gray-900 mb-3">
                            {t('share.peopleWithoutMail')}
                          </h4>
                          <div className="space-y-3">
                            {peopleWithoutEmail.map(person => (
                              <button
                                key={person.id}
                                onClick={() =>
                                  handleSelectPersonWithoutEmail(person)
                                }
                                className="w-full flex items-center gap-4 p-1 rounded-lg hover:bg-gray-50 transition-colors text-left">
                                <Avatar
                                  alt={person.name}
                                  imgClassName="rounded-full w-12 h-12 object-cover"
                                  src={person.profileImage}
                                  size="h-12 w-12"
                                  isHeader={true}
                                />
                                <div>
                                  <p className="font-medium text-gray-900 text-[15px]">
                                    {person.name}
                                  </p>
                                  <p className="text-[13px] text-gray-400">
                                    {t('share.clickToAddMailId')}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No results */}
                      {peopleWithEmail.length === 0 &&
                        peopleWithoutEmail.length === 0 && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {t('share.noMatchingPeopleFound')}
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* General Access Section */}
          {mode !== 'invite' && !isUnshareMode && (
            <div className="mb-6">
              <h3 className="text-[#9CA3AF] font-medium mb-4 text-[15px]">
                {t('share.generalAccess')}
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">
                      {t('share.anyoneWithTheLink')}
                    </p>
                    <p className="text-gray-400 text-[13px]">
                      {link.length > 30 ? `${link.substring(0, 30)}...` : link}
                    </p>
                  </div>
                </div>
                <Button
                  btnStyle="no-outline"
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#8FB358] bg-[#f5faeb] text-[#4a6b32] font-semibold text-[14px] hover:bg-[#ebf5dd] transition-colors">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  {isCopied ? 'Copied!' : 'Copy Link'}
                </Button>
              </div>
            </div>
          )}

          {/* People with Access Section */}
          <div className="mb-4 flex-1 overflow-hidden flex flex-col">
            <h3 className="text-[#9CA3AF] font-medium mb-4 text-[15px] flex-shrink-0">
              {t('share.peopleWithAccess')}
            </h3>
            <div className="space-y-5 overflow-y-auto pr-2 flex-1 pb-4">
              {peopleWithAccessList.map((person: any, index: number) => {
                const isOwner =
                  person?.isOwner ||
                  person?.type === 'owner' ||
                  person?.id === currentUserId;
                return (
                  <div
                    key={person?.id || index}
                    className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar
                        alt={person?.name}
                        imgClassName="rounded-full w-12 h-12 object-cover"
                        src={person?.profileImage}
                        size="h-12 w-12"
                        isHeader={true}
                      />
                      <div>
                        <p className="font-medium text-gray-900 text-[15px]">
                          {person?.name || 'Unknown User'}
                          {isOwner && ' (You)'}
                        </p>
                        {mode === 'invite' ? (
                          <p className="text-[13px] text-gray-400 mt-0.5 capitalize">
                            {isOwner ? 'Owner' : person?.role || 'Contributor'}
                          </p>
                        ) : (
                          person?.email && (
                            <p className="text-[13px] text-gray-400 mt-0.5">
                              {person.email}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                    {isOwner ? (
                      <div className="flex items-center gap-2 text-[#2196F3] font-semibold text-[14px]">
                        <div className="w-[18px] h-[18px] bg-[#2196F3] rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                        <span>Owner</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        {/* Inline Role Selector solely for Invite Mode */}
                        {mode === 'invite' && (
                          <div className="flex items-center gap-1">
                            <span className="text-[#4a6b32] font-semibold text-[14px] capitalize">
                              {person?.role || 'Contributor'}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={() =>
                            handleShowRemoveConfirmation(person?.id)
                          }
                          className="text-[#E53E3E] hover:text-[#C53030] transition-colors">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-8 pb-8 pt-4 border-t border-gray-100 flex-shrink-0">
          <div className="flex justify-center gap-4">
            <Button
              btnStyle="no-outline"
              onClick={handleDiscard}
              className="w-44 py-2.5 rounded-lg font-semibold text-[15px] border border-[#8FB358] bg-[#f5faeb] text-[#4a6b32] hover:bg-[#ebf5dd] transition-colors text-center">
              {t('share.discardChanges')}
            </Button>
            <Button
              btnStyle="no-outline"
              onClick={handleSave}
              disabled={isInviting}
              className="w-44 py-2.5 rounded-lg font-semibold text-[15px] bg-[#4a6b32] text-white hover:bg-[#3d592a] transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed">
              {mode === 'invite'
                ? isInviting
                  ? t('share.inviting')
                  : t('share.invite')
                : isUnshareMode
                  ? t('done')
                  : t('share.saveChanges')}
            </Button>
          </div>
        </div>
      </div>

      {/* Remove Confirmation Dialog */}
      <ConfirmationBox
        isOpen={showRemoveConfirm}
        onCancel={() => {
          setShowRemoveConfirm(false);
          setPersonToRemove(null);
        }}
        onClose={() => {
          setShowRemoveConfirm(false);
          setPersonToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        headerTitle={t('share.removeAccess')}
        title={t('share.removeConfirmTitle')}
        description={t('share.removeConfirmDescription')}
        icon={<SVGAssets.Delete className="w-20 h-20 text-[#7F9D48] pt-3" />}
        confirmText={t('deleteIt')}
        cancelText={t('keepIt')}
      />
    </div>
  );
};
